import React from 'react'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Form from 'antd/lib/form'
import Input from 'antd/lib/input'
import Button from 'antd/lib/button'
import message from 'antd/lib/message'
import Divider from 'antd/lib/divider'
import Tooltip from 'antd/lib/tooltip'
import PageHeader from 'antd/lib/page-header'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { SettingProps } from '@slerp/controls'
import { useMutation, useApolloClient } from '@apollo/client'
import {
  UPDATE_GENERAL_SETTINGS,
  CREATE_COMPANY_ADDRESS,
  UPDATE_COMPANY_ADDRESS
} from './actions'
import styled from '@emotion/styled'
import AddressInput from './../Locations/LocationForm/AddressInput'
import { isEmpty } from 'lodash'
import { uuid } from 'uuidv4'
import { isValidUKPhoneNumber } from './../Locations/LocationForm/helpers'
import { companySuffixRule, companyRegisteredNumberRule } from './Rules'
import MobileAppStoreInput from './MobileAppStoreInput'
import { handleCompanyWebsiteFormat } from 'components/Login/utils'
import { onBlurScrollNumber, onFocusScrollNumber } from 'components/Utils/price'
import AddressForm from 'components/Locations/GeneralSettings/Forms/AddressForm'
import { AddressHash } from 'components/Locations/LocationForm/DetailsForm'

export const UKPhoneValidatorRule = () => ({
  async validator(_: any, value: string) {
    if (!value) {
      return Promise.reject('Contact number is required')
    }

    if (isValidUKPhoneNumber('+44' + value)) {
      return Promise.resolve()
    }

    return Promise.reject('Please input a valid UK phone number')
  }
})

const { useForm } = Form

const GeneralSettings = (props: SettingProps) => {
  const { merchant } = props
  const [form] = useForm()

  const [updateSetting, { loading }] = useMutation(UPDATE_GENERAL_SETTINGS, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      const values = form.getFieldsValue()
      const {
        company_phone_number,
        registered_company_address,
        address_line_1,
        address_line_2,
        address_city,
        address_postcode,
        address_country,
        address_lat,
        address_lng
      } = values

      const hasNoCompanyAddress = isEmpty(merchant.address)

      //guard clause to only do a mutation if there is a valid input on registered_company_address
      if (
        hasNoCompanyAddress &&
        !registered_company_address.formattedAddress &&
        !company_phone_number
      )
        return

      const companyAddressMutation = hasNoCompanyAddress
        ? createCompanyAddress
        : updateCompanyAddress

      companyAddressMutation({
        variables: {
          ...(hasNoCompanyAddress && { id: uuid() }),
          merchant_id: merchant.id,
          line_1: address_line_1,
          line_2: address_line_2,
          city: address_city,
          country: address_country,
          zip: address_postcode,
          company_phone_number: `+44${company_phone_number}`,
          geom: {
            type: 'Point',
            crs: {
              type: 'name',
              properties: {
                name: 'urn:ogc:def:crs:EPSG::4326'
              }
            },
            coordinates: [address_lat, address_lng]
          }
        }
      })
        .then(() => {
          message.destroy()
          message.success(
            'Company Information has been successfully updated!',
            2
          )
        })
        .catch(() =>
          message.error(
            `Unable to update company information due to ${
              hasNoCompanyAddress
                ? errorCreateCompanyAddress
                : errorUpdateCompanyAddress
            }.`,
            5
          )
        )
        .finally(() => client.resetStore())
    }
  })

  const [
    createCompanyAddress,
    { loading: loadingCreateCompanyAddress, error: errorCreateCompanyAddress }
  ] = useMutation(CREATE_COMPANY_ADDRESS, {
    fetchPolicy: 'no-cache'
  })

  const [
    updateCompanyAddress,
    { loading: loadingUpdateCompanyAddress, error: errorUpdateCompanyAddress }
  ] = useMutation(UPDATE_COMPANY_ADDRESS, {
    fetchPolicy: 'no-cache'
  })

  const client = useApolloClient()

  const onFinish = (values: any) => {
    const {
      company_name,
      company_email,
      company_number,
      vat_id,
      email,
      website,
      name,
      registered_company_name,
      cro_registered_number
    } = values

    const formattedUrl = handleCompanyWebsiteFormat(website)

    form
      .validateFields()
      .then(() => {
        message.loading('Updating... Please wait.')
        updateSetting({
          variables: {
            id: merchant.id,
            settings: {
              company_name,
              company_email,
              company_number,
              vat_id,
              email,
              website: formattedUrl
            },
            name: name,
            registered_company_name: registered_company_name,
            cro_registered_number: cro_registered_number
          }
        })
          .then((result) => {
            message.destroy()
            message.success('Merchant Updated', 2)
          })
          .catch((error) =>
            message.error(`Unable to update settings due to ${error}.`, 5)
          )
      })
      .catch((error) =>
        message.error(`Unable to update settings due to ${error}.`, 5)
      )
  }

  const updateAddressFields = (addressHash: AddressHash) => {
    form.setFieldsValue({
      address_line_1: addressHash.line1,
      address_line_2: addressHash.line2,
      address_city: addressHash.city,
      address_postcode: addressHash.postalCode,
      address_country: addressHash.country,
      address_lat: addressHash.lat,
      address_lng: addressHash.lng
    })
  }

  return (
    <Row>
      <Col span={15}>
        <Form
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          form={form}
          layout={'vertical'}
          onFinish={onFinish}
          initialValues={{
            name: merchant.name,
            email: merchant.setting.email,
            website: merchant.setting.website,
            slug: merchant.slug,
            company_email: merchant.setting.company_email,
            vat_id: merchant.setting.vat_id,
            registered_company_name: merchant.registered_company_name,
            cro_registered_number: merchant.cro_registered_number,
            company_phone_number: merchant.address?.contact_num.substring(3),
            apple_store_url: merchant.app_setting.apple_store_url,
            google_play_url: merchant.app_setting.google_play_url,
            registered_company_address: {
              formattedAddress: merchant.address
                ? [
                    merchant.address?.line_1,
                    merchant.address?.line_2,
                    merchant.address?.city,
                    merchant.address?.zip
                  ]
                    .filter(Boolean)
                    .join(', ')
                : ''
            },
            address_line_1: merchant.address?.line_1 || '',
            address_line_2: merchant.address?.line_2 || '',
            address_city: merchant.address?.city || '',
            address_country: merchant.address?.country || '',
            address_postcode: merchant.address?.zip || '',
            address_lat: (merchant.address?.geom?.coordinates || [])[0],
            address_lng: (merchant.address?.geom?.coordinates || [])[1]
          }}
        >
          <PageHeader title='General Settings' />

          <Form.Item name='name' label='Company Name:'>
            <Input required data-testid='general-settings-company-name-field' />
          </Form.Item>

          <Form.Item
            name='email'
            label={
              <>
                Email:
                <Tooltip
                  title={
                    <span>
                      Ensure this is the best email to use to receive Slerp news
                      and updates.
                    </span>
                  }
                >
                  <QuestionCircleOutlined className='_ml-8' />
                </Tooltip>
              </>
            }
            rules={[
              {
                type: 'email',
                message: 'Please input a valid email'
              }
            ]}
          >
            <Input data-testid='general-settings-email-field' />
          </Form.Item>

          <Form.Item name='website' label='Website:'>
            <Input data-testid='general-settings-website-field' />
          </Form.Item>

          <Form.Item
            name='slug'
            label={
              <>
                SlerpSpace Domain:
                <Tooltip
                  title={
                    <span>
                      Talk to a member of our support team if you wish to
                      change.
                    </span>
                  }
                >
                  <QuestionCircleOutlined className='_ml-8' />
                </Tooltip>
              </>
            }
          >
            <Input
              prefix='https://'
              suffix='.slerp.com'
              disabled
              data-testid='slerpspace-domain-field'
            />
          </Form.Item>

          {merchant.app_setting.apple_store_url && (
            <MobileAppStoreInput
              label='Apple Store'
              tooltipTitle='This is the URL for your app that customers will use to download the app from the App Store'
              type='apple_store_url'
            />
          )}

          {merchant.app_setting.google_play_url && (
            <MobileAppStoreInput
              label='Google Play'
              tooltipTitle='This is the URL for your app that customers will use to download the app from Google Play'
              type='google_play_url'
            />
          )}

          <Divider />

          <PageHeader
            title={
              <>
                Trading Details
                <Tooltip
                  title={
                    <span>
                      Make sure all trading information has been provided, as we
                      will use the information to ensure that customer receipts
                      are valid.
                    </span>
                  }
                >
                  <QuestionCircleOutlined className='_ml-8' />
                </Tooltip>
              </>
            }
          />

          <Form.Item
            name='registered_company_name'
            label='Registered Company Name:'
            rules={companySuffixRule}
          >
            <Input data-testid='registered-company-name-field' />
          </Form.Item>

          <Form.Item
            name='registered_company_address'
            label='Registered Company Address:'
          >
            <AddressInput onSelectCallback={updateAddressFields} />
          </Form.Item>

          <AddressForm addressLineBlurCallback={() => form.validateFields()} />

          <Form.Item
            label='Company Phone Number:'
            className='sentry-block'
            data-sentry-block
          >
            <Input.Group>
              <Row gutter={8} className='_mb-0'>
                <Col span={5}>
                  <Input defaultValue='+44' disabled />
                </Col>
                <Col span={19}>
                  <Form.Item
                    name='company_phone_number'
                    noStyle
                    rules={[UKPhoneValidatorRule]}
                  >
                    <Input
                      placeholder='7911123456'
                      type='number'
                      data-testid='company-phone-number-field'
                      onFocus={onFocusScrollNumber}
                      onBlur={onBlurScrollNumber}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Input.Group>
          </Form.Item>

          <Form.Item
            name='cro_registered_number'
            label='Company Registration Number:'
            rules={companyRegisteredNumberRule}
          >
            <Input data-testid='company-registration-number-field' />
          </Form.Item>

          <Form.Item
            name='company_email'
            label={
              <>
                Accounts Email:
                <Tooltip
                  title={
                    <span>
                      This email address will receive invoices and
                      payment-related notifications
                    </span>
                  }
                >
                  <QuestionCircleOutlined className='_ml-8' />
                </Tooltip>
              </>
            }
            rules={[
              {
                required: true,
                message: 'Email is required'
              },
              {
                type: 'email',
                message: 'Please input a valid email'
              }
            ]}
          >
            <Input data-testid='accounts-email-field' />
          </Form.Item>

          <Form.Item name='vat_id' label='Vat ID:'>
            <Input data-testid='vat-id-field' />
          </Form.Item>

          <Form.Item>
            <Submit>
              <Button
                data-testid='general-settings-save-btn'
                title='Save'
                type='primary'
                size='large'
                htmlType='submit'
                className='_ml-8'
                loading={
                  loading ||
                  loadingUpdateCompanyAddress ||
                  loadingCreateCompanyAddress
                }
              >
                Save
              </Button>
            </Submit>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  )
}

const Submit = styled.div(() => ({
  textAlign: 'right'
}))

export default GeneralSettings
